import React, { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './EmailCampaign.scss';

const EmailCampaign = ({ onClose, onSave, editingCampaign = null }) => {
  const [campaignData, setCampaignData] = useState({
    name: editingCampaign?.name || '',
    subject: editingCampaign?.subject || '',
    fromName: editingCampaign?.fromName || '',
    fromEmail: editingCampaign?.fromEmail || '',
    content: editingCampaign?.content || '',
    segment: editingCampaign?.segment || 'all_candidates',
    scheduledAt: editingCampaign?.scheduledAt || '',
    isScheduled: editingCampaign?.isScheduled || false,
    template: editingCampaign?.template || 'custom'
  });

  const [activeTab, setActiveTab] = useState('content');

  // ReactQuill configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['blockquote', 'code-block'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'align',
    'link', 'image', 'blockquote', 'code-block'
  ];

  // Email templates
  const emailTemplates = {
    welcome: {
      name: 'Welcome Email',
      subject: 'Welcome to {{company_name}}!',
      content: `Dear {{candidate_name}},

Welcome to {{company_name}}! We're excited to have you in our talent community.

We've received your application for the {{position_name}} position and wanted to let you know what happens next:

• Our team will review your application within 2-3 business days
• If you're a good fit, we'll reach out to schedule an interview
• You can track your application status in your candidate portal

In the meantime, feel free to explore our company culture and values on our website.

Best regards,
{{sender_name}}
{{company_name}} Team

---
You're receiving this email because you applied for a position at {{company_name}}.`
    },
    interview_invite: {
      name: 'Interview Invitation',
      subject: 'Interview Invitation - {{position_name}}',
      content: `Dear {{candidate_name}},

Thank you for your interest in the {{position_name}} position at {{company_name}}.

We were impressed with your background and would like to invite you for an interview.

Interview Details:
• Position: {{position_name}}
• Date: {{interview_date}}
• Time: {{interview_time}}
• Duration: {{interview_duration}}
• Location: {{interview_location}}
• Interviewer: {{interviewer_name}}

To confirm your attendance, please reply to this email or use the link below:
{{confirmation_link}}

If this time doesn't work for you, please let us know your availability for the next few days.

We look forward to meeting you!

Best regards,
{{sender_name}}
{{company_name}} Team`
    },
    application_update: {
      name: 'Application Status Update',
      subject: 'Application Update - {{position_name}}',
      content: `Dear {{candidate_name}},

We wanted to update you on the status of your application for the {{position_name}} position at {{company_name}}.

Status: {{application_status}}

{{status_message}}

{{next_steps}}

If you have any questions, please don't hesitate to reach out to us.

Best regards,
{{sender_name}}
{{company_name}} Team

---
Application ID: {{application_id}}`
    },
    follow_up: {
      name: 'Follow-up Email',
      subject: 'Following up on your application',
      content: `Dear {{candidate_name}},

I hope this email finds you well. I wanted to follow up on your application for the {{position_name}} position at {{company_name}}.

We received your application on {{application_date}} and are currently reviewing all submissions. Due to the high volume of applications, our review process is taking a bit longer than usual.

We expect to complete the initial review by {{expected_response_date}} and will be in touch with next steps.

Thank you for your patience and continued interest in {{company_name}}.

Best regards,
{{sender_name}}
{{company_name}} Team`
    }
  };

  // Candidate segments
  const segmentOptions = [
    { value: 'all_candidates', label: 'All Candidates', count: 1234 },
    { value: 'new_applicants', label: 'New Applicants (Last 7 days)', count: 89 },
    { value: 'interview_scheduled', label: 'Interview Scheduled', count: 45 },
    { value: 'interview_completed', label: 'Interview Completed', count: 23 },
    { value: 'pending_response', label: 'Pending Response', count: 156 },
    { value: 'active_prospects', label: 'Active Prospects', count: 234 },
    { value: 'inactive_candidates', label: 'Inactive Candidates (30+ days)', count: 456 }
  ];

  // Available merge tags
  const mergeTags = [
    { tag: '{{candidate_name}}', description: 'Candidate\'s full name' },
    { tag: '{{first_name}}', description: 'Candidate\'s first name' },
    { tag: '{{last_name}}', description: 'Candidate\'s last name' },
    { tag: '{{email}}', description: 'Candidate\'s email address' },
    { tag: '{{position_name}}', description: 'Applied position title' },
    { tag: '{{company_name}}', description: 'Your company name' },
    { tag: '{{sender_name}}', description: 'Your name' },
    { tag: '{{application_date}}', description: 'Date of application' },
    { tag: '{{application_id}}', description: 'Unique application ID' },
    { tag: '{{interview_date}}', description: 'Interview date' },
    { tag: '{{interview_time}}', description: 'Interview time' },
    { tag: '{{application_status}}', description: 'Current application status' }
  ];

  const handleSave = () => {
    if (!campaignData.name.trim()) {
      alert('Please enter a campaign name');
      return;
    }
    if (!campaignData.subject.trim()) {
      alert('Please enter an email subject');
      return;
    }
    // Check for content - ReactQuill returns HTML, so we need to check for actual content
    const contentText = campaignData.content.replace(/<[^>]*>/g, '').trim();
    if (!contentText || contentText === '') {
      alert('Please enter email content');
      return;
    }

    onSave({
      ...campaignData,
      type: 'email',
      createdAt: new Date().toISOString()
    });
  };

  const handleTemplateSelect = (templateKey) => {
    const template = emailTemplates[templateKey];
    // Convert plain text template to HTML for ReactQuill
    const htmlContent = template.content.replace(/\n/g, '<br>');
    
    setCampaignData(prev => ({
      ...prev,
      template: templateKey,
      subject: template.subject,
      content: htmlContent
    }));
  };

  const insertMergeTag = (tag) => {
    // For ReactQuill, we'll append the merge tag to the current content
    const currentContent = campaignData.content;
    const newContent = currentContent + ' ' + tag + ' ';
    
    setCampaignData(prev => ({
      ...prev,
      content: newContent
    }));
  };

  const getEstimatedReach = () => {
    const segment = segmentOptions.find(s => s.value === campaignData.segment);
    return segment ? segment.count : segmentOptions[0]?.count || 0;
  };

  return (
    <div className="email-campaign-modal">
      <div className="email-campaign-container">
        {/* Header */}
        <div className="campaign-header">
          <div className="header-content">
            <h2>📧 {editingCampaign ? 'Edit Email Campaign' : 'Create Email Campaign'}</h2>
            <p>Design and send targeted email campaigns to your candidates</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              {editingCampaign ? 'Update Campaign' : 'Save Campaign'}
            </button>
          </div>
        </div>

        <div className="campaign-content">
          {/* Navigation Tabs */}
          <div className="campaign-tabs">
            <button
              className={`tab-btn ${activeTab === 'content' ? 'active' : ''}`}
              onClick={() => setActiveTab('content')}
            >
              📝 Content
            </button>
            <button
              className={`tab-btn ${activeTab === 'audience' ? 'active' : ''}`}
              onClick={() => setActiveTab('audience')}
            >
              🎯 Audience
            </button>
            <button
              className={`tab-btn ${activeTab === 'schedule' ? 'active' : ''}`}
              onClick={() => setActiveTab('schedule')}
            >
              📅 Schedule
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'content' && (
            <div className="tab-content">
              <div className="content-section">
                {/* Campaign Settings */}
                <div className="form-section">
                  <h3>Campaign Details</h3>
                  
                  <div className="form-group">
                    <label>Campaign Name *</label>
                    <input
                      type="text"
                      value={campaignData.name}
                      onChange={(e) => setCampaignData(prev => ({
                        ...prev,
                        name: e.target.value
                      }))}
                      placeholder="e.g., Welcome New Candidates"
                      className="form-input"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>From Name *</label>
                      <input
                        type="text"
                        value={campaignData.fromName}
                        onChange={(e) => setCampaignData(prev => ({
                          ...prev,
                          fromName: e.target.value
                        }))}
                        placeholder="Your Name"
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>From Email *</label>
                      <input
                        type="email"
                        value={campaignData.fromEmail}
                        onChange={(e) => setCampaignData(prev => ({
                          ...prev,
                          fromEmail: e.target.value
                        }))}
                        placeholder="your@company.com"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Email Subject *</label>
                    <input
                      type="text"
                      value={campaignData.subject}
                      onChange={(e) => setCampaignData(prev => ({
                        ...prev,
                        subject: e.target.value
                      }))}
                      placeholder="Enter email subject line"
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Email Templates */}
                <div className="templates-section">
                  <h3>Email Templates</h3>
                  <div className="templates-grid">
                    <div
                      className={`template-card ${campaignData.template === 'custom' ? 'active' : ''}`}
                      onClick={() => setCampaignData(prev => ({ ...prev, template: 'custom' }))}
                    >
                      <div className="template-icon">📝</div>
                      <div className="template-name">Custom Email</div>
                      <div className="template-desc">Start from scratch</div>
                    </div>
                    
                    {Object.entries(emailTemplates).map(([key, template]) => (
                      <div
                        key={key}
                        className={`template-card ${campaignData.template === key ? 'active' : ''}`}
                        onClick={() => handleTemplateSelect(key)}
                      >
                        <div className="template-icon">📧</div>
                        <div className="template-name">{template.name}</div>
                        <div className="template-desc">Pre-built template</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Email Content */}
                <div className="content-editor-section">
                  <div className="content-header">
                    <h3>Email Content</h3>
                    <div className="merge-tags-dropdown">
                      <button className="merge-tags-btn">
                        + Insert Merge Tag
                      </button>
                      <div className="merge-tags-list">
                        {mergeTags.map(tag => (
                          <div
                            key={tag.tag}
                            className="merge-tag-item"
                            onClick={() => insertMergeTag(tag.tag)}
                          >
                            <code>{tag.tag}</code>
                            <span>{tag.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <ReactQuill
                      theme="snow"
                      value={campaignData.content}
                      onChange={(content) => setCampaignData(prev => ({
                        ...prev,
                        content: content
                      }))}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Enter your email content here..."
                      className="email-content-editor"
                      style={{ height: '300px' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'audience' && (
            <div className="tab-content">
              <div className="audience-section">
                <h3>Select Audience</h3>
                
                <div className="segments-list">
                  {segmentOptions.map(segment => (
                    <div
                      key={segment.value}
                      className={`segment-item ${campaignData.segment === segment.value ? 'selected' : ''}`}
                      onClick={() => setCampaignData(prev => ({
                        ...prev,
                        segment: segment.value
                      }))}
                    >
                      <div className="segment-info">
                        <div className="segment-name">{segment.label}</div>
                        <div className="segment-description">
                          {segment.count} candidates will receive this email
                        </div>
                      </div>
                      <div className="segment-count">{segment.count}</div>
                    </div>
                  ))}
                </div>

                <div className="audience-summary">
                  <div className="summary-card">
                    <div className="summary-icon">🎯</div>
                    <div className="summary-info">
                      <div className="summary-title">Estimated Reach</div>
                      <div className="summary-value">{getEstimatedReach().toLocaleString()} candidates</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="tab-content">
              <div className="schedule-section">
                <h3>Send Options</h3>
                
                <div className="send-options">
                  <div
                    className={`send-option ${!campaignData.isScheduled ? 'selected' : ''}`}
                    onClick={() => setCampaignData(prev => ({ ...prev, isScheduled: false }))}
                  >
                    <div className="option-radio">
                      <div className={`radio ${!campaignData.isScheduled ? 'checked' : ''}`}></div>
                    </div>
                    <div className="option-info">
                      <div className="option-title">Send Immediately</div>
                      <div className="option-desc">Email will be sent right after saving</div>
                    </div>
                  </div>
                  
                  <div
                    className={`send-option ${campaignData.isScheduled ? 'selected' : ''}`}
                    onClick={() => setCampaignData(prev => ({ ...prev, isScheduled: true }))}
                  >
                    <div className="option-radio">
                      <div className={`radio ${campaignData.isScheduled ? 'checked' : ''}`}></div>
                    </div>
                    <div className="option-info">
                      <div className="option-title">Schedule for Later</div>
                      <div className="option-desc">Choose a specific date and time</div>
                    </div>
                  </div>
                </div>

                {campaignData.isScheduled && (
                  <div className="schedule-datetime">
                    <div className="form-group">
                      <label>Scheduled Date & Time</label>
                      <input
                        type="datetime-local"
                        value={campaignData.scheduledAt}
                        onChange={(e) => setCampaignData(prev => ({
                          ...prev,
                          scheduledAt: e.target.value
                        }))}
                        min={new Date().toISOString().slice(0, 16)}
                        className="form-input"
                      />
                    </div>
                  </div>
                )}

                <div className="schedule-summary">
                  <div className="summary-card">
                    <div className="summary-icon">📅</div>
                    <div className="summary-info">
                      <div className="summary-title">Delivery Time</div>
                      <div className="summary-value">
                        {campaignData.isScheduled && campaignData.scheduledAt
                          ? new Date(campaignData.scheduledAt).toLocaleString()
                          : 'Immediately after save'
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


        </div>
      </div>
    </div>
  );
};

export default EmailCampaign;